import { FieldArray, FieldHookConfig, useField } from 'formik'
import { useMemo, useState } from 'react'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Button } from 'blaze-ui/Button'
import { ChevronDown, ChevronUp, Trash2 } from 'blaze-ui/icons'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import Styles from './FormikArrayTextAreaInput.module.scss'
import { InputContainerProps, InputSize } from 'almanac-ui/TextInputField/Types'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { ButtonSize } from 'blaze-ui/Button/Types'
import cx from 'classnames'
import { useHover } from '@react-aria/interactions'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props {
  showMore: boolean
  onShowMore: () => void
  onShowLess: () => void
  minValue?: number
  size?: InputSize
  disabled?: boolean
  onRemove?: (text: string, name: string) => void
  onAdd?: (text: string, name: string) => void
  initialVisibleCount?: number
  onItemPress?: (index: number, value: string) => void
}

const InputContainer = ({
  onRemove,
  isRemoveDisabled,
  removeDisabledTooltip,
  size = 'xs',
  onClick,
  ...inputProps
}: InputContainerProps & {
  onRemove: () => void
  isRemoveDisabled?: boolean
  removeDisabledTooltip?: string
  onClick?: () => void
}) => {
  const { isMobile } = useResponsiveDesign()
  const { hoverProps, isHovered } = useHover({})
  const [isFocused, setIsFocused] = useState(false)
  let buttonSize
  if (size === 'lg') {
    buttonSize = 'md'
  } else if (size === 'xl') {
    buttonSize = 'lg'
  } else {
    buttonSize = 'xs'
  }

  const showTooltip = (!isFocused && isHovered && !isMobile) || isMobile

  return (
    <div
      className={Styles.inputContainer}
      {...hoverProps}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      {showTooltip && !onClick && (
        <Tooltip label={removeDisabledTooltip} hide={!isRemoveDisabled}>
          <div className={cx(Styles.closeButton, Styles[buttonSize])}>
            <IconButton
              variant="ghost"
              size={buttonSize as ButtonSize}
              icon={Trash2}
              color="var(--dark-80)"
              isDisabled={isRemoveDisabled}
              onPress={onRemove}
            />
          </div>
        </Tooltip>
      )}
      <Input
        isTextarea
        onFocusChange={setIsFocused}
        size={size}
        {...inputProps}
        onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
        className={cx(Styles.input, { [Styles.readOnlyInput]: !!onClick })}
      />
    </div>
  )
}

/**
 * After a show more/less toggle, the DOM reflows and a topic row from a
 * neighboring section can end up under the user's finger, causing a phantom
 * click. This captures and swallows the very next click at the document level.
 */
const swallowNextClick = () => {
  const handler = (e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }
  document.addEventListener('click', handler, { capture: true, once: true })
  setTimeout(() => document.removeEventListener('click', handler, { capture: true }), 500)
}

export const FormikArrayTextAreaInput = ({
  showMore,
  onShowLess,
  onShowMore,
  minValue = 0,
  size = 'sm',
  disabled = false,
  onRemove,
  onAdd,
  initialVisibleCount = 5,
  onItemPress,
  ...props
}: FieldHookConfig<string[]> & Props) => {
  const [field] = useField(props)

  const values = useMemo(() => {
    return showMore ? field.value : field.value.slice(0, initialVisibleCount)
  }, [showMore, field.value, initialVisibleCount])

  const hasMore = field.value.length > initialVisibleCount

  const handleToggle = (action: () => void) => {
    swallowNextClick()
    action()
  }

  return (
    <FieldArray name={props.name}>
      {({ replace, remove }) => (
        <>
          {values.map((value, index) => (
            <InputContainer
              isRemoveDisabled={disabled || field.value.length <= minValue}
              removeDisabledTooltip="Cannot have fewer topics than posts"
              key={index}
              onRemove={() => {
                const topicText = value.trim()
                onRemove?.(topicText, field.name)
                remove(index)
              }}
              size={size}
              name={`${field.name}.${index}`}
              value={value}
              autoFocus={!onItemPress && value.trim().length === 0}
              disabled={disabled}
              readOnly={!!onItemPress}
              onChange={(e) => {
                if (!onItemPress) {
                  return replace(index, e.target.value)
                }
              }}
              onBlur={(e) => {
                if (onItemPress) return
                const topicText = e.target.value.trim()
                onAdd?.(topicText, field.name)
                if (field.value.length > minValue && topicText.length < 1) {
                  remove(index)
                }
              }}
              onClick={onItemPress ? () => onItemPress(index, value) : undefined}
            />
          ))}
          {!showMore && hasMore && (
            <div className={Styles.showMoreButton}>
              <Button variant="tertiary" size="sm" onPress={() => handleToggle(onShowMore)} endIcon={ChevronDown}>
                Show {field.value.length - initialVisibleCount} More
              </Button>
            </div>
          )}
          {showMore && hasMore && (
            <div className={Styles.showMoreButton}>
              <Button variant="tertiary" size="sm" onPress={() => handleToggle(onShowLess)} endIcon={ChevronUp}>
                Show less
              </Button>
            </div>
          )}
        </>
      )}
    </FieldArray>
  )
}

export const ControlledFormikArrayTextAreaInput = (
  props: FieldHookConfig<string[]> & Omit<Props, 'showMore' | 'onShowMore' | 'onShowLess'>,
) => {
  const [showMore, setShowMore] = useState(false)

  return (
    <FormikArrayTextAreaInput
      showMore={showMore}
      onShowMore={() => setShowMore(true)}
      onShowLess={() => setShowMore(false)}
      {...props}
    />
  )
}
